import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { closed, onKey, type ListboxState } from './listbox.ts';

export type SelectOption = { value: string; label: string; lang?: string; dir?: 'ltr' | 'rtl' };

/**
 * `LocaleProvider` remounts the page when the locale changes, which drops the
 * focus a keyboard user chose with. The trigger that made the choice takes it
 * back if its replacement mounts within this long.
 */
const REFOCUS_MS = 10_000;
let chosen: { id: string; at: number } | null = null;

/**
 * The app's one dropdown: a select-only combobox (WAI-ARIA APG) on daisyUI's
 * `dropdown`. Not a native `select`, because daisyUI 5.7.36 styles a select's
 * open list only under `@supports (appearance: base-select)`, and Safari and
 * Firefox drew it bare.
 *
 * The trigger is a `button`, so a `<label htmlFor>` reaches it as it reached
 * the select. Open and closed are state rather than daisyUI's `:focus-within`,
 * because the trigger keeps focus while the list is shut. `value` is `null`
 * for nothing chosen, shown as `placeholder`.
 */
export function Select({
  id,
  value,
  options,
  onChange,
  disabled,
  placeholder,
  ghost,
  className = '',
  ...aria
}: {
  id?: string;
  value: string | null;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** The signed-out corner's borderless trigger, rather than a form field's. */
  ghost?: boolean;
  className?: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  'aria-required'?: true;
}) {
  const ownId = useId();
  const triggerId = id ?? ownId;
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [state, setState] = useState<ListboxState>(closed);
  const selected = options.findIndex((o) => o.value === value);
  const open = state.open && !disabled;
  const current = options[selected];
  const labels = options.map((o) => o.label);
  const optionId = (i: number) => `${triggerId}-option-${i}`;

  useEffect(() => {
    if (chosen?.id === triggerId && Date.now() - chosen.at < REFOCUS_MS) trigger.current?.focus();
    chosen = null;
  }, [triggerId]);

  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setState(closed);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  useEffect(() => {
    if (open) document.getElementById(optionId(state.active))?.scrollIntoView({ block: 'nearest' });
  });

  const choose = (i: number) => {
    setState(closed);
    trigger.current?.focus();
    if (i === selected) return;
    chosen = { id: triggerId, at: Date.now() };
    onChange(options[i]!.value);
  };

  const keyDown = (e: KeyboardEvent) => {
    const r = onKey(open ? state : closed, e.key, { labels, selected, now: Date.now(), disabled });
    if (r.handled) e.preventDefault();
    setState(r.state);
    if (r.choose !== undefined) choose(r.choose);
  };

  return (
    <div
      ref={root}
      className={`dropdown dropdown-end ${open ? 'dropdown-open' : 'dropdown-close'} ${ghost ? '' : 'w-full min-w-0'} ${className}`}
      onBlur={(e) => {
        if (!root.current?.contains(e.relatedTarget as Node | null)) setState(closed);
      }}
    >
      <button
        ref={trigger}
        id={triggerId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${triggerId}-list`}
        aria-activedescendant={open ? optionId(state.active) : undefined}
        {...aria}
        disabled={disabled}
        // daisyUI drops the outline of a focused dropdown trigger; the
        // keyboard needs it back.
        className={`${ghost ? 'btn btn-ghost font-normal' : `input w-full min-w-0 ${className}`} focus-visible:outline-primary min-h-(--hit-min) cursor-pointer justify-between gap-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid`}
        onClick={() => setState(open ? closed : { ...closed, open: true, active: Math.max(selected, 0) })}
        onKeyDown={keyDown}
      >
        <span className="min-w-0 truncate" lang={current?.lang} dir={current?.dir}>
          {current?.label ?? placeholder}
        </span>
        <ChevronDown aria-hidden className="h-4 w-4 shrink-0 opacity-70" />
      </button>
      <ul
        id={`${triggerId}-list`}
        role="listbox"
        aria-labelledby={aria['aria-label'] ? undefined : (aria['aria-labelledby'] ?? triggerId)}
        aria-label={aria['aria-label']}
        className={`dropdown-content menu bg-base-100 rounded-box border-base-200 z-10 mt-1 max-h-72 flex-nowrap overflow-y-auto border p-2 shadow-sm ${ghost ? 'w-52' : 'w-full'}`}
      >
        {options.map((o, i) => (
          <li key={o.value} role="none">
            <div
              id={optionId(i)}
              role="option"
              aria-selected={i === selected}
              lang={o.lang}
              dir={o.dir}
              className={`flex min-h-(--hit-min) items-center justify-between ${open && i === state.active ? 'menu-focus' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              // Move, not enter: a list opened by the keyboard under a resting
              // pointer fires `mouseenter`, and took the active option from
              // the keys (seen in the hand run: Enter chose the hovered row).
              onMouseMove={() => i !== state.active && setState({ ...closed, open: true, active: i })}
              onClick={() => choose(i)}
            >
              <span>{o.label}</span>
              {i === selected && <Check aria-hidden className="h-4 w-4 shrink-0" />}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
